import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Eye, Calendar, User, Tag, ArrowRight, Loader2 } from 'lucide-react';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import api from '../utils/api';

export default function BlogDetailPage() {
  const { id } = useParams();
  const [post, setPost]     = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/blog/${id}`).then(r => {
      setPost(r.data);
      return api.get(`/blog?limit=3&category=${r.data.category}`);
    }).then(r => setRelated((r.data.items||[]).filter(p=>p._id!==id).slice(0,3))).catch(()=>{}).finally(()=>setLoading(false));
  }, [id]);

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 size={32} className="animate-spin text-royal-800"/></div>;
  if (!post) return <div className="min-h-screen flex flex-col items-center justify-center gap-4"><p className="text-gray-500">Post not found.</p><Link to="/blog" className="btn-royal text-sm px-5 py-2.5">Back to Blog</Link></div>;

  return (
    <div className="min-h-screen bg-gray-50 font-body">
      <Navbar />
      <div className="pt-20">
        <div className="relative bg-hero-gradient overflow-hidden">
          {post.image && <img src={post.image} alt={post.title} className="absolute inset-0 w-full h-full object-cover opacity-25" />}
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
          <div className="relative max-w-4xl mx-auto px-6 py-14 pb-10">
            <Link to="/blog" className="inline-flex items-center gap-2 text-white/60 hover:text-white text-sm mb-6 transition-colors"><ArrowLeft size={15}/>Back to Blog</Link>
            <span className="inline-block px-3 py-1 bg-gold-500 text-royal-950 text-xs font-bold rounded-full uppercase tracking-wide mb-4 capitalize">{post.category}</span>
            <h1 className="font-display text-3xl lg:text-4xl font-bold text-white leading-tight mb-5">{post.title}</h1>
            <div className="flex flex-wrap gap-4 text-sm text-white/50">
              {post.authorName && <span className="flex items-center gap-1.5"><User size={14}/>{post.authorName}</span>}
              {post.publishedAt && <span className="flex items-center gap-1.5"><Calendar size={14}/>{new Date(post.publishedAt).toLocaleDateString('en-NG',{day:'numeric',month:'long',year:'numeric'})}</span>}
              <span className="flex items-center gap-1.5"><Eye size={14}/>{post.views||0} views</span>
            </div>
          </div>
        </div>

        <div className="max-w-4xl mx-auto px-6 py-12">
          {post.excerpt && <div className="border-l-4 border-gold-500 pl-5 bg-gold-50 rounded-r-xl py-4 pr-4 mb-8 text-gray-700 italic text-lg leading-relaxed">{post.excerpt}</div>}
          <div className="prose max-w-none text-gray-700 leading-relaxed text-base whitespace-pre-wrap">{post.content}</div>
          {post.tags?.length > 0 && (
            <div className="mt-8 pt-6 border-t border-gray-200 flex items-center gap-2 flex-wrap">
              <Tag size={14} className="text-gray-400"/>{post.tags.map(t=><span key={t} className="px-3 py-1 bg-royal-50 text-royal-700 rounded-full text-xs font-semibold">#{t}</span>)}
            </div>
          )}
        </div>

        {related.length > 0 && (
          <div className="bg-gray-100 py-12">
            <div className="max-w-7xl mx-auto px-6">
              <h2 className="font-display text-2xl font-bold text-gray-900 mb-6">Related Articles</h2>
              <div className="grid sm:grid-cols-3 gap-5">
                {related.map(p => (
                  <Link key={p._id} to={`/blog/${p._id}`} className="group bg-white rounded-2xl shadow-card hover:shadow-card-hover transition-all duration-300 hover:-translate-y-1 overflow-hidden">
                    <div className="h-36 bg-hero-gradient relative">{p.image&&<img src={p.thumbImage||p.image} alt={p.title} className="w-full h-full object-cover opacity-70 group-hover:scale-105 transition-transform duration-700"/>}</div>
                    <div className="p-4"><h4 className="font-display font-semibold text-sm text-gray-900 line-clamp-2 group-hover:text-royal-800 transition-colors mb-2">{p.title}</h4><span className="text-xs font-semibold text-royal-800 flex items-center gap-1">Read more<ArrowRight size={12}/></span></div>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
